import { ProgramPlanBadge as DesignSystemProgramPlanBadge, Tooltip } from '@quickmd-llc/design-system'
import { getProgramPlanBadgeLabel } from '@quickmd-llc/frontend-utils'
import { getProgramPlanExpiryLabel } from './getProgramPlanExpiryLabel'
import { useProgramPlanPackage } from '@/hooks/useProgramPlanPackage'

type Props = {
  className?: string
  /** The patient whose plans to read. `null` while the chart is still loading. */
  patientId: string | null | undefined
}

/**
 * `Quit 7-OH Plan: 3 visits remaining` — the chip that tells staff this patient
 * is on the Kratom / 7-OH support plan and what is left of it (QMD-4144). It sits
 * on the patient chart header and by the new-visit Visit Summary, and on hover it
 * surfaces the plan's expiry date so staff can see how long that coverage lasts
 * without leaving the page.
 *
 * The chip is `@quickmd-llc/design-system`'s and renders nothing when the patient
 * holds no usable plan, so this can be dropped into a tag row unconditionally
 * rather than repeating the guard there. What this wrapper adds is the fetch, the
 * kill switch, and the expiry tooltip — all admin's, not the design system's.
 *
 * The tooltip wraps the chip only when the chip is actually drawn *and* there is
 * an expiry to show: `getProgramPlanBadgeLabel` is the DS chip's own render gate,
 * so a spent or lapsed plan draws nothing, and a tooltip whose trigger drew
 * nothing is a hover target hanging over empty space.
 */
export function ProgramPlanBadge({ className, patientId }: Props) {
  const programPackage = useProgramPlanPackage(patientId)
  const expiryLabel = getProgramPlanExpiryLabel(programPackage)

  const badge = <DesignSystemProgramPlanBadge className={className} programPackage={programPackage} />

  if (!getProgramPlanBadgeLabel(programPackage) || !expiryLabel) {
    return badge
  }

  return <Tooltip content={expiryLabel}>{badge}</Tooltip>
}
